import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BackButton } from '../components/ui';

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow rounded-lg p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <BackButton
                onClick={() => navigate('/legal')}
                variant="link"
                ariaLabel="Back to Legal Center"
              />
              <span className="text-blue-600 hover:text-blue-500 font-medium">
                Back to Legal Center
              </span>
            </div>
          </div>

          <div className="prose max-w-none">
            <div className="mb-6">
              <p className="text-sm text-gray-500 mb-4">
                Effective Date: [Insert Date]
              </p>
              <p className="text-gray-700 mb-6">
                Vesta values your privacy. This Privacy Policy explains how we collect, use, share, and protect your personal information when you use our platform ("Platform"), including our website, apps, and services for buying, selling, or delivering food and related services.
              </p>
              <p className="text-gray-700 mb-6">
                By using Vesta, you agree to the terms of this Privacy Policy and our <Link to="/legal/terms" className="text-blue-600 hover:text-blue-500">Terms of Service</Link>.
              </p>
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Information We Collect</h2>
            <p className="text-gray-700 mb-4">We collect information from you in the following ways:</p>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">1.1 Information You Provide Directly</h3>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2"><strong>Account Information</strong> – name, email address, phone number, password, profile photo.</li>
              <li className="mb-2"><strong>Seller Compliance Details</strong> – licenses, permits, certification numbers, tax information, business name, kitchen location.</li>
              <li className="mb-2"><strong>Order & Transaction Data</strong> – purchase history, delivery addresses, payment method details (stored via our payment processor, not Vesta).</li>
              <li className="mb-2"><strong>Content You Upload</strong> – videos, images, descriptions, comments, reviews, and messages.</li>
              <li className="mb-2"><strong>Communications</strong> – when you contact Vesta support or participate in surveys.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">1.2 Information We Collect Automatically</h3>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2"><strong>Device & Usage Data</strong> – IP address, browser type, operating system, app version, device identifiers.</li>
              <li className="mb-2"><strong>Activity Data</strong> – pages viewed, searches, clicks, time spent, interaction with videos or listings.</li>
              <li className="mb-2"><strong>Location Data</strong> – precise location if you enable GPS, otherwise approximate location based on IP address.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">1.3 Information from Third Parties</h3>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2"><strong>Payment Processors</strong> – to confirm payments, prevent fraud, and resolve disputes.</li>
              <li className="mb-2"><strong>Identity Verification Providers</strong> – to confirm seller or buyer identity and prevent misuse.</li>
              <li className="mb-2"><strong>Advertising & Analytics Partners</strong> – to measure and improve marketing performance.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. How We Use Your Information</h2>
            <p className="text-gray-700 mb-4">We process your personal information for the following purposes:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">To create and manage your account.</li>
              <li className="mb-2">To process orders, payments, and deliveries.</li>
              <li className="mb-2">To verify seller compliance with applicable laws.</li>
              <li className="mb-2">To personalize your experience, including recommendations and search results.</li>
              <li className="mb-2">To communicate with you about your orders, account, and platform updates.</li>
              <li className="mb-2">To provide customer service and dispute resolution.</li>
              <li className="mb-2">To improve and secure our Platform.</li>
              <li className="mb-2">To comply with legal obligations and enforce our policies.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Cookies & Tracking Technologies</h2>
            <p className="text-gray-700 mb-4">We use cookies, pixel tags, and similar technologies to:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">Keep you signed in.</li>
              <li className="mb-2">Remember your preferences.</li>
              <li className="mb-2">Analyze Platform usage and performance.</li>
              <li className="mb-2">Serve personalized content and advertising.</li>
            </ul>
            <p className="text-gray-700 mb-6">
              You can control cookies via your browser settings, but disabling them may affect site functionality.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. How We Share Your Information</h2>
            <p className="text-gray-700 mb-4">We share your information only as necessary:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2"><strong>With Other Users</strong> – sellers receive buyer order and contact details; buyers see seller profile and compliance details.</li>
              <li className="mb-2"><strong>With Service Providers</strong> – payment processors, hosting providers, delivery couriers, cloud storage, analytics providers.</li>
              <li className="mb-2"><strong>For Legal Reasons</strong> – when required by law, subpoena, or to protect rights and safety.</li>
              <li className="mb-2"><strong>With Your Consent</strong> – e.g., when you link your account to a social media platform.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Data Security</h2>
            <p className="text-gray-700 mb-6">
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
            <p className="text-gray-700 mb-6">
              We retain your personal information for as long as necessary to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements. Account information is retained while your account is active and for a reasonable period after deactivation.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Your Rights</h2>
            <p className="text-gray-700 mb-4">Depending on your location, you may have the right to:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">Access, correct, or delete your personal information.</li>
              <li className="mb-2">Restrict or object to processing of your data.</li>
              <li className="mb-2">Data portability.</li>
              <li className="mb-2">Withdraw consent where processing is based on consent.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. International Transfers</h2>
            <p className="text-gray-700 mb-6">
              Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for such transfers in accordance with applicable data protection laws.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Children's Privacy</h2>
            <p className="text-gray-700 mb-6">
              Our Platform is not intended for children under 18. We do not knowingly collect personal information from children under 18. If you believe we have collected such information, please contact us immediately.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Changes to This Policy</h2>
            <p className="text-gray-700 mb-6">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the effective date. Your continued use of the Platform after such changes constitutes acceptance of the updated policy.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Contact Us</h2>
            <p className="text-gray-700 mb-6">
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p className="text-gray-700">
                <strong>Vesta Privacy Team</strong><br />
                Email: privacy@vesta.com<br />
                Address: [Insert Vesta Business Address]
              </p>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy; 